import { FC, useMemo } from 'react';

import {
   CombinedFacetV2Response,
   FacetKeysV2,
   facetLabelTranslationMappingV2,
   ExtendedFacetWithTermsV2
} from '@ravago/shared/page-data/models/elastic';
import { DynamicPageUrl } from '@ravago/shared/page-data/models/utils';
import { Box } from '@ravago/shared/radiance/components/box/components/Box/Box';
import { SearchItem } from '@ravago/shared/radiance/components/multifacet/components/SearchItem/SearchItem';
import { HighlightText } from '@ravago/shared/radiance/components/typography/components/HighlightText/HighlightText';
import { Typography } from '@ravago/shared/radiance/components/typography/components/Typography/Typography';
import { useTranslations } from '@ravago/shared/translations/client';

import { AppUtils } from '../../../../utils/app/app.utils';
import { extendedFacetWithTermsToQueryV2 } from '../../../content/product-portfolio/utils/filter-query-utils';

interface Props {
   suggestedFacets: CombinedFacetV2Response[];
   itemCount: number;
   highlightTerm: string;
   locale: string;
   onExtendedFacetClick?: (extendedFacet: Array<ExtendedFacetWithTermsV2>) => void;
   facetType: 'apply' | 'navigate';
   productOverviewRoute?: DynamicPageUrl;
}

export const AutoSuggestFiltersV2: FC<Props> = ({
   suggestedFacets,
   itemCount,
   highlightTerm,
   locale,
   onExtendedFacetClick,
   facetType,
   productOverviewRoute
}) => {
   const translate = useTranslations();

   const toExtendedFacetsWithTermsV2 = (facetV2Response: CombinedFacetV2Response, parentKey?: string): Array<ExtendedFacetWithTermsV2> => {
      const extendedFacetsWithTermsV2s: Array<ExtendedFacetWithTermsV2> = [];

      Object.keys(facetV2Response).forEach((facetKey) => {
         const facetItems = facetV2Response[facetKey as FacetKeysV2] ?? [];
         facetItems.forEach((facet) => {
            const existing = extendedFacetsWithTermsV2s.find((f) => f.facetName === facetKey);
            if (existing) {
               existing.terms.push({
                  label: facet.label,
                  key: facet.key,
                  parentKey,
                  nested: facet?.nested ? toExtendedFacetsWithTermsV2(facet.nested, facet.key) : undefined
               });
            } else {
               extendedFacetsWithTermsV2s.push({
                  facetName: facetKey,
                  terms: [
                     {
                        label: facet.label,
                        key: facet.key,
                        parentKey,
                        nested: facet?.nested ? toExtendedFacetsWithTermsV2(facet.nested, facet.key) : undefined
                     }
                  ]
               });
            }
         });
      });

      return extendedFacetsWithTermsV2s;
   };

   const extendedFacets = useMemo(() => suggestedFacets.map((facet) => toExtendedFacetsWithTermsV2(facet)), [suggestedFacets]);

   const facetTerms = (facet: ExtendedFacetWithTermsV2, index: number, isLast: boolean) => (
      <Box key={AppUtils.getKey(facet.facetName, index)} direction="column" gap="3xs" justify="center" height="100%">
         {(facet as ExtendedFacetWithTermsV2).terms.map((facetTerm, facetTermIndex) => {
            if (facetTerm.nested && facetTerm.nested.length > 0) {
               return (
                  <Box key={AppUtils.getKey(facetTerm.key, facetTermIndex)} gap="none">
                     <Typography variant="small-highlight" color="primary">
                        {facetTerm.nested && facetTerm.nested.length > 0
                           ? translate(facetLabelTranslationMappingV2.get(facetTerm.nested[0].facetName) ?? '')
                           : ''}
                     </Typography>
                     &nbsp;
                     <Typography variant="small" color="strong">
                        {facetTerm.nested[0].terms.map((nestedTerm, i) => (
                           <Typography variant="inherit" color="inherit" component="span" key={AppUtils.getKey(nestedTerm.key, i)}>
                              <HighlightText text={`${nestedTerm.label} - ${facetTerm.label}`} searchValue={highlightTerm} />
                              {facetTerm.nested && facetTerm.nested[0] && i !== facetTerm.nested[0].terms.length - 1 ? ',' : ''}
                           </Typography>
                        ))}
                        {!isLast ? ',' : ''}
                     </Typography>
                  </Box>
               );
            }

            return (
               <Box key={AppUtils.getKey(facetTerm.key, facetTermIndex)} gap="none">
                  <Typography variant="small-highlight" color="primary">
                     {translate(facetLabelTranslationMappingV2.get(facet.facetName) ?? facet.facetName)}
                  </Typography>
                  &nbsp;
                  <Typography variant="small" color="strong">
                     <HighlightText text={facetTerm.label} searchValue={highlightTerm} />
                     {!isLast ? ',' : ''}
                  </Typography>
               </Box>
            );
         })}
      </Box>
   );

   const facetItem = (item: ExtendedFacetWithTermsV2[]) => (
      <SearchItem trailingIcon="plus" onClick={() => onExtendedFacetClick?.(item)}>
         <Box direction="column" gap="3xs" justify="center" height="100%">
            {item.map((facet, index) => facetTerms(facet, index, index === item.length - 1))}
         </Box>
      </SearchItem>
   );

   const getRoute = (combinedFacets: ExtendedFacetWithTermsV2[]) => {
      const queryParams = new URLSearchParams(extendedFacetWithTermsToQueryV2(combinedFacets)).toString();

      if (productOverviewRoute) {
         return `${locale}/${productOverviewRoute.url}?${queryParams}`;
      }

      return '';
   };

   return (
      <Box gap="lg" width="100%">
         <Box direction="column" gap="lg" width="100%">
            <Typography variant="heading-4">{translate('auto-suggest.filters')}</Typography>
            <Box direction="column" gap="xs" width="100%">
               {extendedFacets.slice(0, itemCount).map((item, index) => (
                  <Box
                     key={AppUtils.getKey(
                        item
                           .flatMap((f) =>
                              f.terms.map((i) => {
                                 if ('term' in i) return i.term;
                                 if ('label' in i) return i.label;
                                 return '';
                              })
                           )
                           .join(''),
                        index
                     )}
                     direction="column"
                     items="start"
                     gap="2xs"
                  >
                     {facetType === 'apply' ? (
                        facetItem(item)
                     ) : (
                        <a href={getRoute(item) ?? undefined} style={{ display: 'contents' }}>
                           {facetItem(item)}
                        </a>
                     )}
                  </Box>
               ))}
               {extendedFacets.length === 0 && <Typography>{translate('auto-suggest.no-filters')}</Typography>}
            </Box>
         </Box>
      </Box>
   );
};
